import {useMemo} from 'react';
import useSWR from 'swr';
import {useSwrApi} from '@app/modules/api/swr-legacy-api';
import {useApiCall} from '@deezer/react-legacy-api';
import {useTranslation} from '@app/hooks/useTranslation';
import {
	deletePermissions,
	getClientById,
	getUserPermissions,
} from '@app/modules/oauth/client';
import {useAutolog} from './auth/useAutolog';
import {useAppState} from '@app/modules/apps/context';

type Applications = {
	APPLICATIONS: {
		APP_ID: string;
		APP_PICTURE: string;
		NAME: string;
		PERMISSIONS: {
			DATE: string;
			PERMISSIONS: string;
		};
	}[];
};

export type Permission = {
	appDescription?: string;
	appId: string;
	appName: string;
	logoThemeDarkUrl?: string | null;
	logoThemeLightUrl?: string | null;
	permissions: {accepted_at: string; scopes: string | string[]};
	logo?: string;
	isLegacy?: boolean;
};

const useGetLegacyPermissions = () => {
	const {appName} = useAppState();

	// Call deezer_checkform first to set the api_token
	const {data: checkformData} = useSwrApi({
		method: 'deezer_checkform',
		data: {APP_NAME: appName},
	});

	// Only fetch application_list after checkform has set the token
	const {data, error, mutate} = useSwrApi(
		{method: 'application_list'},
		{
			cacheKey: checkformData ? '{"method":"application_list"}' : null,
		},
	);

	const permissions = useMemo(() => {
		if (data) {
			const newPermissions: Permission[] = [];
			(data as Applications)['APPLICATIONS'].map((app) => {
				newPermissions.push({
					appId: app['APP_ID'],
					appName: app['NAME'],
					permissions: {
						accepted_at: app['PERMISSIONS']['DATE'],
						scopes: app['PERMISSIONS']['PERMISSIONS'],
					},
					logo: app['APP_PICTURE'],
					isLegacy: true,
				});
			});
			return newPermissions;
		}
	}, [data]);

	return {
		permissions: data ? permissions : [],
		error,
		legacyMutate: mutate,
	};
};

export const usePermission = () => {
	const t = useTranslation();
	const apiCall = useApiCall();
	const {permissions: legacyPermissions, legacyMutate} =
		useGetLegacyPermissions();
	const {data} = useAutolog(false);
	const {data: permissions, mutate} = useSWR(
		data?.jwt ? data?.jwt : null,
		fetchPermissions,
	);
	const sortedPermissions = useMemo(
		() => sortPermissions(legacyPermissions ?? [], permissions ?? []),
		[legacyPermissions, permissions],
	);

	const isLegacy = (clientId: string) => {
		return sortedPermissions.find((p) => p.appId === clientId)?.isLegacy;
	};

	const revokePermissions = async (clientId: string) => {
		try {
			if (isLegacy(clientId)) {
				const res = await apiCall({
					method: 'application.remove',
					data: {
						app_id: clientId,
					},
				});
				if (res && legacyMutate) {
					legacyMutate();
				}
			} else {
				if (!data?.jwt) return;
				const response = await deletePermissions(clientId, data?.jwt);
				if (!response.ok) {
					throw new Error();
				}
				mutate();
			}
		} catch {
			const error = new Error(t('error.anerroroccurredpleasetryagain'));
			error.name = t('error.error');
			throw error;
		}
	};

	return {permissions: sortedPermissions, revokePermissions};
};

const fetchPermissions = async (jwt: string) => {
	const permissions = await getUserPermissions(jwt);
	if (permissions) {
		const appIds = Object.keys(permissions);
		const appsPromise = Promise.all(
			appIds.map(async (appId) => {
				const appData = await getClientById(appId);
				return appData;
			}),
		);
		const apps = await appsPromise;
		const userPermissions = [] as Permission[];
		apps.map((app) => {
			userPermissions.push({
				appDescription: app.clientComment,
				appId: app.clientId,
				appName: app.clientName,
				logoThemeDarkUrl: app.options.logoThemeDarkUrl,
				logoThemeLightUrl: app.options.logoThemeLightUrl,
				permissions: permissions[app.clientId],
			});
		});
		return userPermissions;
	}
	return [];
};

function sortPermissions(
	legacyPermissions: Permission[],
	oauthPermissions: Permission[],
) {
	if (legacyPermissions && oauthPermissions) {
		const sortedPermissions = [...legacyPermissions, ...oauthPermissions].sort(
			descendingDateSort,
		);
		return sortedPermissions;
	}
	return [];
}

function descendingDateSort(a: Permission, b: Permission) {
	const dateA = new Date(a.permissions.accepted_at);
	const dateB = new Date(b.permissions.accepted_at);
	return dateB.getTime() - dateA.getTime();
}
